import React, { useState, useEffect } from 'react';
import { employeeAPI } from '../lib/api';
import { formatINR, formatINRShort, getStatusBadgeClass, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import DatePicker from '../components/DatePicker';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Users, 
  DollarSign,
  Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DEPARTMENTS = [
  'Branding',
  'Marketing',
  'Operations',
  'Sales',
  'Video Editing',
  'Designing',
  'Video Production',
  'Advertising'
];

const SKILLS = [
  'Brand Strategy',
  'Visual Design',
  'Digital Marketing',
  'SEO',
  'Content Strategy',
  'Premiere Pro',
  'After Effects',
  'Figma',
  'Adobe XD',
  'Prototyping',
  'Production Management',
  'Scripting',
  'Media Buying',
  'Campaign Analytics',
  'Sales',
  'Negotiation',
  'Project Management'
];

export default function Employees() {
  const { can } = useAuth();
  const canWrite = can('write');
  const canDelete = can('delete');
  const [employees, setEmployees] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    department: '',
    role: '',
    monthly_salary: '',
    date_of_joining: '',
    skills: [],
    status: 'active',
    email: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, statsRes] = await Promise.all([
        employeeAPI.getAll(),
        employeeAPI.getDepartmentStats()
      ]);
      setEmployees(empRes.data);
      setDepartmentStats(statsRes.data);
    } catch (error) {
      toast.error(apiError(error, 'Failed to fetch employees'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      department: '',
      role: '',
      monthly_salary: '',
      date_of_joining: '',
      skills: [],
      status: 'active',
      email: ''
    });
    setEditingEmployee(null);
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      department: employee.department,
      role: employee.role,
      monthly_salary: employee.monthly_salary.toString(),
      date_of_joining: employee.date_of_joining,
      skills: employee.skills || [],
      status: employee.status,
      email: employee.email || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        monthly_salary: parseFloat(formData.monthly_salary)
      };

      if (editingEmployee) {
        await employeeAPI.update(editingEmployee.id, data);
        toast.success('Employee updated successfully');
      } else {
        await employeeAPI.create(data);
        toast.success('Employee added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to save employee'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    
    try {
      await employeeAPI.delete(id);
      toast.success('Employee deleted');
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to delete employee'));
    }
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || emp.department === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  const totalSalary = employees.filter(e => e.status === 'active').reduce((sum, e) => sum + e.monthly_salary, 0);
  const activeCount = employees.filter(e => e.status === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Employees</h1>
          <p className="text-gray-500 mt-1">Manage your team and track department costs</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          {canWrite && (
            <DialogTrigger asChild>
              <Button data-testid="add-employee-btn" className="bg-black hover:bg-black/90">
                <Plus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="employee-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Select 
                    value={formData.department} 
                    onValueChange={(v) => setFormData({ ...formData, department: v })}
                  >
                    <SelectTrigger data-testid="employee-department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role/Designation *</Label>
                  <Input
                    id="role"
                    data-testid="employee-role-input"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="salary">Monthly Salary (₹) *</Label>
                  <Input
                    id="salary"
                    type="number"
                    data-testid="employee-salary-input"
                    value={formData.monthly_salary}
                    onChange={(e) => setFormData({ ...formData, monthly_salary: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="doj">Date of Joining *</Label>
                  <DatePicker
                    id="doj"
                    value={formData.date_of_joining}
                    onChange={(v) => setFormData({ ...formData, date_of_joining: v })}
                    placeholder="Pick joining date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger data-testid="employee-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="emp-email">Work email (Microsoft 365)</Label>
                <Input
                  id="emp-email"
                  type="email"
                  placeholder="name@deseno.co.in"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <p className="text-xs text-gray-400">Used to auto-match this person's PC in Workforce. Should match the M365 account they log into Windows/Mac with.</p>
              </div>

              {formData.monthly_salary && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Per Day Working Cost</p>
                  <p className="text-xl font-bold font-mono">{formatINR(parseFloat(formData.monthly_salary) / 30)}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Skills</Label>
                <div className="flex flex-wrap gap-2">
                  {SKILLS.map(skill => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => toggleSkill(skill)}
                      aria-pressed={formData.skills.includes(skill)}
                      className={`px-3 py-2 sm:py-1 text-xs rounded-full transition-colors ${
                        formData.skills.includes(skill)
                          ? 'bg-black text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingEmployee ? 'Update' : 'Add Employee'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <Users className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{activeCount}</p>
                <p className="text-sm text-gray-500">Active Employees</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{formatINR(totalSalary)}</p>
                <p className="text-sm text-gray-500">Monthly Salaries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{formatINR(totalSalary / (activeCount || 1))}</p>
                <p className="text-sm text-gray-500">Avg. Salary</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Stats Chart */}
      {departmentStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Department Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={departmentStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="department" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={formatINRShort} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'total_salary' ? formatINR(value) : value,
                    name === 'total_salary' ? 'Total Salary' : 'Headcount'
                  ]}
                  contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '8px', color: '#fff' }}
                />
                <Bar dataKey="total_salary" fill="#000000" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="employee-search"
            placeholder="Search by name or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterDepartment} onValueChange={setFilterDepartment}>
          <SelectTrigger className="w-[200px]" data-testid="department-filter">
            <SelectValue placeholder="Filter by department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {DEPARTMENTS.map(dept => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="table-header text-left">Name</th>
                  <th className="table-header text-left">Department</th>
                  <th className="table-header text-left">Role</th>
                  <th className="table-header text-right">Monthly Salary</th>
                  <th className="table-header text-right">Per Day Cost</th>
                  <th className="table-header text-center">Status</th>
                  {(canWrite || canDelete) && <th className="table-header text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employee) => (
                    <tr key={employee.id} className="table-row" data-testid={`employee-row-${employee.id}`}>
                      <td className="table-cell font-medium truncate max-w-[200px]" title={employee.name}>{employee.name}</td>
                      <td className="table-cell text-gray-600">{employee.department}</td>
                      <td className="table-cell text-gray-600">{employee.role}</td>
                      <td className="table-cell text-right font-mono">{formatINR(employee.monthly_salary)}</td>
                      <td className="table-cell text-right font-mono">{formatINR(employee.per_day_cost)}</td>
                      <td className="table-cell text-center">
                        <span className={`badge ${getStatusBadgeClass(employee.status)}`}>
                          {employee.status}
                        </span>
                      </td>
                      {(canWrite || canDelete) && (
                        <td className="table-cell">
                          <div className="flex items-center justify-center gap-2">
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(employee)}
                                data-testid={`edit-employee-${employee.id}`}
                                aria-label={`Edit ${employee.name}`}
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(employee.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-employee-${employee.id}`}
                                aria-label={`Delete ${employee.name}`}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canWrite || canDelete ? 7 : 6} className="text-center py-12 text-gray-400">
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
